import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { config } from '../config';
import type { UserProfile } from '../api/client';
import { getStoredOAuthIdToken, clearStoredOAuthSession } from '../lib/authStorage';

/** Dispatched by API client when any auth request gets 401; we sign out so user can sign in again. */
export const AUTH_SESSION_EXPIRED = 'auth:session-expired';

type AuthUser = { email: string; sub: string };
type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; user: AuthUser; idToken: string; profile: UserProfile | null; sessionInvalid?: boolean };

let authTokenGetter: (() => Promise<string | null>) | null = null;

/** Called by API client to get the current id token for admin requests. */
export function getAuthToken(): Promise<string | null> {
  return authTokenGetter ? authTokenGetter() : Promise.resolve(null);
}

export function setAuthTokenGetter(fn: (() => Promise<string | null>) | null) {
  authTokenGetter = fn;
}

const AuthContext = createContext<{
  state: AuthState;
  profile: UserProfile | null;
  /** True when server rejected our token (401 on getMe); user should sign out and sign in again. */
  sessionInvalid: boolean;
  refreshProfile: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => void;
  getIdToken: () => Promise<string | null>;
} | null>(null);

function getPool() {
  const { cognitoUserPoolId, cognitoClientId } = config;
  if (!cognitoUserPoolId || !cognitoClientId) return null;
  return new CognitoUserPool({
    UserPoolId: cognitoUserPoolId,
    ClientId: cognitoClientId,
  });
}

function getCurrentUser(): CognitoUser | null {
  const pool = getPool();
  if (!pool) return null;
  return pool.getCurrentUser();
}

/** Cognito JWT iss is e.g. "https://cognito-idp.us-east-1.amazonaws.com/us-east-1_xxx". Ensure token is from the pool we're configured for. */
function tokenIssuerMatchesConfig(idToken: string): boolean {
  const expected = config.cognitoUserPoolId?.trim();
  if (!expected) return false;
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1])) as { iss?: string };
    const iss = typeof payload?.iss === 'string' ? payload.iss : '';
    return iss.endsWith('/' + expected) || iss.includes(expected);
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refreshSession = useCallback(() => {
    const pool = getPool();
    const user = getCurrentUser();
    if (pool && user) {
      user.getSession((err: Error | null, session: { isValid: () => boolean; getIdToken: () => { getJwtToken: () => string } } | null) => {
        if (err || !session || !session.isValid()) {
          const oauthToken = getStoredOAuthIdToken();
          if (oauthToken && tokenIssuerMatchesConfig(oauthToken)) {
            try {
              const payload = JSON.parse(atob(oauthToken.split('.')[1]));
              const exp = payload.exp * 1000;
              if (exp > Date.now()) {
                setState({
                  status: 'authenticated',
                  user: { email: payload.email || payload['cognito:username'] || '', sub: payload.sub },
                  idToken: oauthToken,
                  profile: null,
                });
                return;
              }
            } catch {
              clearStoredOAuthSession();
            }
          }
          if (oauthToken && !tokenIssuerMatchesConfig(oauthToken)) clearStoredOAuthSession();
          setState({ status: 'unauthenticated' });
          return;
        }
        const idToken = session.getIdToken().getJwtToken();
        if (!tokenIssuerMatchesConfig(idToken)) {
          setState({ status: 'unauthenticated' });
          return;
        }
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        setState({
          status: 'authenticated',
          user: { email: payload.email || payload['cognito:username'] || '', sub: payload.sub },
          idToken,
          profile: null,
        });
      });
      return;
    }
    const oauthToken = getStoredOAuthIdToken();
    if (oauthToken && tokenIssuerMatchesConfig(oauthToken)) {
      try {
        const payload = JSON.parse(atob(oauthToken.split('.')[1]));
        const exp = payload.exp * 1000;
        if (exp > Date.now()) {
          setState({
            status: 'authenticated',
            user: { email: payload.email || payload['cognito:username'] || '', sub: payload.sub },
            idToken: oauthToken,
            profile: null,
          });
          return;
        }
      } catch {
        clearStoredOAuthSession();
      }
    }
    if (oauthToken && !tokenIssuerMatchesConfig(oauthToken)) clearStoredOAuthSession();
    setState({ status: 'unauthenticated' });
  }, []);

  useEffect(() => {
    if (!config.cognitoUserPoolId || !config.cognitoClientId) {
      setState({ status: 'unauthenticated' });
      return;
    }
    refreshSession();
  }, [refreshSession]);

  // Register token getter first so any API call (including getMe) sees the token immediately.
  useEffect(() => {
    setAuthTokenGetter(() =>
      state.status === 'authenticated' ? Promise.resolve(state.idToken) : Promise.resolve(null)
    );
    return () => setAuthTokenGetter(null);
  }, [state]);

  const refreshProfile = useCallback(async () => {
    if (state.status !== 'authenticated') return;
    try {
      const { userApi } = await import('../api/client');
      const profile = await userApi.getMe();
      setState((prev) => (prev.status === 'authenticated' ? { ...prev, profile, sessionInvalid: false } : prev));
    } catch (e) {
      const is401 = (e instanceof Error && e.message?.includes('401')) || String(e).includes('401');
      setState((prev) =>
        prev.status === 'authenticated'
          ? { ...prev, profile: null, sessionInvalid: is401 }
          : prev
      );
    }
  }, [state.status]);

  useEffect(() => {
    if (state.status === 'authenticated' && state.profile === null && !state.sessionInvalid) {
      refreshProfile();
    }
  }, [state, refreshProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    const pool = getPool();
    if (!pool) throw new Error('Cognito not configured');
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    await new Promise<void>((resolve, reject) => {
      user.authenticateUser(new AuthenticationDetails({ Username: email.toLowerCase(), Password: password }), {
        onSuccess: (session: { isValid: () => boolean; getIdToken: () => { getJwtToken: () => string } } | null) => {
          if (!session || !session.isValid()) {
            reject(new Error('Session could not be retrieved. Please try again.'));
            return;
          }
          try {
            const idToken = session.getIdToken().getJwtToken();
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            setState({
              status: 'authenticated',
              user: { email: (payload.email || payload['cognito:username'] || '').toLowerCase(), sub: payload.sub },
              idToken,
              profile: null,
            });
            resolve();
          } catch (e) {
            reject(e instanceof Error ? e : new Error('Could not read session. Please try again.'));
          }
        },
        onFailure: (err) => reject(err),
      });
    });
  }, [refreshSession]);

  const signUp = useCallback(async (email: string, password: string) => {
    const pool = getPool();
    if (!pool) throw new Error('Cognito not configured');
    await new Promise<void>((resolve, reject) => {
      pool.signUp(email.toLowerCase(), password, [new CognitoUserAttribute({ Name: 'email', Value: email })], [], (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }, []);

  const confirmSignUp = useCallback(async (email: string, code: string) => {
    const pool = getPool();
    if (!pool) throw new Error('Cognito not configured');
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    await new Promise<void>((resolve, reject) => {
      user.confirmRegistration(code, true, (err) => (err ? reject(err) : resolve()));
    });
  }, []);

  const doSdkResend = useCallback((email: string) => {
    const pool = getPool();
    if (!pool) throw new Error('Cognito not configured');
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    return new Promise<void>((resolve, reject) => {
      user.resendConfirmationCode((err) => (err ? reject(err) : resolve()));
    });
  }, []);

  const resendVerificationCode = useCallback(async (email: string) => {
    if (config.apiBaseUrl) {
      try {
        const { resendVerificationCodeApi } = await import('../api/client');
        await resendVerificationCodeApi(email);
        return;
      } catch (apiErr) {
        const msg = apiErr instanceof Error ? apiErr.message : String(apiErr);
        const isNetwork = msg === 'Failed to fetch' || msg.includes('fetch') || msg.includes('NetworkError') || msg.includes('Cannot reach the server');
        // ResendConfirmationCode does not accept IAM auth; Cognito returns "Resend not authorized" when called from Lambda. Fall back to SDK (browser → Cognito).
        const isResendNotAuthorized = msg.includes('Resend not authorized') || msg.includes('503');
        if (isNetwork || isResendNotAuthorized) {
          try {
            await doSdkResend(email);
            return;
          } catch (sdkErr) {
            throw sdkErr;
          }
        }
        throw apiErr;
      }
    }
    await doSdkResend(email);
  }, [doSdkResend]);

  const forgotPassword = useCallback(async (email: string) => {
    const pool = getPool();
    if (!pool) throw new Error('Cognito not configured');
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    await new Promise<void>((resolve, reject) => {
      user.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
        // Omit inputVerificationCode so Cognito just sends the email; we collect code + new password on the Reset step.
      });
    });
  }, []);

  const confirmForgotPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    const pool = getPool();
    if (!pool) throw new Error('Cognito not configured');
    const user = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    await new Promise<void>((resolve, reject) => {
      user.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const signOut = useCallback(() => {
    const user = getCurrentUser();
    if (user) user.signOut();
    clearStoredOAuthSession();
    setState({ status: 'unauthenticated' });
  }, []);

  const sessionInvalid = state.status === 'authenticated' && state.sessionInvalid === true;

  // On 401 from any auth API, sign out so we stop sending bad tokens and user can sign in again
  useEffect(() => {
    const onSessionExpired = () => signOut();
    window.addEventListener(AUTH_SESSION_EXPIRED, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED, onSessionExpired);
  }, [signOut]);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (state.status === 'authenticated') return state.idToken;
    const user = getCurrentUser();
    if (!user) return null;
    return new Promise((resolve) => {
      user.getSession((err: Error | null, session: { getIdToken: () => { getJwtToken: () => string } } | null) => {
        if (err || !session) {
          resolve(null);
          return;
        }
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }, [state]);

  const profile = state.status === 'authenticated' ? state.profile : null;

  return (
    <AuthContext.Provider value={{ state, profile, sessionInvalid, refreshProfile, signIn, signUp, confirmSignUp, resendVerificationCode, forgotPassword, confirmForgotPassword, signOut, getIdToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
